import type { ReactNode } from 'react'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { headingReveal } from '../../animations/gsapAnimations'

type SectionHeadingProps = {
  eyebrow?: string
  title: ReactNode
  lede?: string
  /** A link or button that sits opposite the title on desktop. */
  action?: ReactNode
  id?: string
  align?: 'left' | 'center'
  className?: string
  /* The three slots can be tagged from outside — what makes a heading
     clickable in the console's preview frame. Empty on the public site, so
     nothing here changes what a visitor is served. */
  eyebrowProps?: Record<string, string>
  titleProps?: Record<string, string>
  ledeProps?: Record<string, string>
}

/* Every section opens the same way, and reveals the same way: the coachline
   draws itself, the label fades up under it, the headline rises line by
   line from behind its mask, and the lede follows. The choreography is part
   of the component so no caller has to remember to add it — and so every
   section on the site lands on the same beat. */
function SectionHeading({
  eyebrow,
  title,
  lede,
  action,
  id,
  align = 'left',
  className = '',
  eyebrowProps,
  titleProps,
  ledeProps,
}: SectionHeadingProps) {
  const ref = useGsapContext<HTMLDivElement>((scope) => headingReveal(scope))

  const centered = align === 'center'

  return (
    <div
      ref={ref}
      className={`flex flex-col gap-6 ${
        centered
          ? 'items-center text-center'
          : 'sm:flex-row sm:items-end sm:justify-between'
      } ${className}`}
    >
      <div className={centered ? 'max-w-[60ch]' : 'max-w-[52ch]'}>
        {eyebrow ? (
          <p
            data-heading="eyebrow"
            {...eyebrowProps}
            className={`flex items-center gap-3 label text-lime ${
              centered ? 'justify-center' : ''
            }`}
          >
            <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
            {eyebrow}
            {centered ? (
              <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
            ) : null}
          </p>
        ) : null}

        <h2
          id={id}
          data-heading="title"
          {...titleProps}
          className="mt-5 font-display text-display-2 text-platinum"
        >
          {title}
        </h2>

        {lede ? (
          <p
            data-heading="body"
            {...ledeProps}
            className="mt-5 text-[15.5px] leading-relaxed text-steel"
          >
            {lede}
          </p>
        ) : null}
      </div>

      {action ? (
        <div data-heading="body" className="shrink-0">
          {action}
        </div>
      ) : null}
    </div>
  )
}

export default SectionHeading
